import { zodResolver } from '@hookform/resolvers/zod';
import { useIntegrationMqttGetAllBrokers, type MqttBroker } from '@smarthome/api';
import { AlertCircle, Check, ChevronDown, HelpCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { TextField, TextareaField, Tooltip, cn } from '@/components/ui';

const inputFormSchema = z.object({
  brokerId: z.string().min(1, 'Broker is required'),
  name: z.string().max(255).optional(),
  topic: z.string().min(1, 'Topic is required').max(500),
  inputId: z.string().min(1, 'Input ID is required').max(255),
  payloadFilter: z.string().max(1000).optional(),
  payloadExtract: z.string().max(1000).optional(),
  payloadTransform: z.string().max(1000).optional(),
});

export type InputFormData = z.infer<typeof inputFormSchema>;

export interface MqttInputFormProps {
  formId: string;
  initialValues?: Partial<InputFormData>;
  preselectedBrokerId?: string;
  onSubmit: (data: InputFormData) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  brokers?: MqttBroker[];
}

export function MqttInputForm({
  formId,
  initialValues,
  preselectedBrokerId,
  onSubmit,
  isLoading: _isLoading,
  error,
  brokers: providedBrokers,
}: MqttInputFormProps) {
  const { data: brokersData } = useIntegrationMqttGetAllBrokers({
    query: { enabled: !providedBrokers },
  });
  const brokers = providedBrokers ?? brokersData?.brokers ?? [];

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<InputFormData>({
    resolver: zodResolver(inputFormSchema),
    defaultValues: {
      brokerId: initialValues?.brokerId ?? preselectedBrokerId ?? '',
      name: initialValues?.name ?? '',
      topic: initialValues?.topic ?? '',
      inputId: initialValues?.inputId ?? '',
      payloadFilter: initialValues?.payloadFilter ?? '',
      payloadExtract: initialValues?.payloadExtract ?? '',
      payloadTransform: initialValues?.payloadTransform ?? '',
    },
  });

  const payloadExtractExample = '{"temp": $.t, "hum": $.h}';
  const payloadTransformExample = '{"temp": $.temp, "status": "ok"}';

  const payloadExtractHelp = (
    <div className="space-y-1 text-xs">
      <p>JSONata can return objects to extract multiple values.</p>
      <code className="block font-mono">{payloadExtractExample}</code>
      <p>Transform runs on this result, so use $.temp.</p>
    </div>
  );

  const payloadTransformHelp = (
    <div className="space-y-1 text-xs">
      <p>Transform runs on the extracted value (or full payload if no extract).</p>
      <code className="block font-mono">{payloadTransformExample}</code>
      <p>Leave both empty to send an event without a payload.</p>
    </div>
  );

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <TextField
        label="Name"
        placeholder="Optional label"
        error={errors.name?.message}
        {...register('name')}
      />

      {!preselectedBrokerId && (
        <Controller
          name="brokerId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <BrokerSelect
              label="Broker"
              value={value}
              onChange={onChange}
              brokers={brokers}
              error={errors.brokerId?.message}
            />
          )}
        />
      )}

      <TextField
        label="Topic"
        placeholder="buttons/001/state"
        error={errors.topic?.message}
        {...register('topic')}
      />

      <TextField
        label="Input ID"
        placeholder="button-001"
        error={errors.inputId?.message}
        {...register('inputId')}
      />

      <TextField
        label="Payload filter (JSONata)"
        placeholder='$.action = "double"'
        error={errors.payloadFilter?.message}
        {...register('payloadFilter')}
      />

      <TextField
        label="Payload extract (JSONata)"
        placeholder="$.state"
        labelAccessory={
          <Tooltip content={payloadExtractHelp} className="max-w-[280px]">
            <button
              type="button"
              aria-label="Payload extract help"
              className="inline-flex items-center rounded p-0.5 text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </Tooltip>
        }
        error={errors.payloadExtract?.message}
        {...register('payloadExtract')}
      />

      <TextareaField
        label="Payload transform (JSONata)"
        placeholder='{"level": $}'
        labelAccessory={
          <Tooltip content={payloadTransformHelp} className="max-w-[280px]">
            <button
              type="button"
              aria-label="Payload transform help"
              className="inline-flex items-center rounded p-0.5 text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </Tooltip>
        }
        error={errors.payloadTransform?.message}
        {...register('payloadTransform')}
      />

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          {error}
        </div>
      )}
    </form>
  );
}

interface BrokerSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  brokers: MqttBroker[];
  error?: string;
}

function BrokerSelect({ label, value, onChange, brokers, error }: BrokerSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedBroker = brokers.find((b) => b.id === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedBroker ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedBroker?.name ?? 'Select broker...'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {brokers.map((broker) => (
              <button
                key={broker.id}
                type="button"
                onClick={() => {
                  onChange(broker.id ?? '');
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === broker.id && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === broker.id && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {broker.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-zinc-500">
                    {broker.host}:{broker.port}
                  </div>
                </div>
                {value === broker.id && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}
